import React from 'react'

import Box from '@mui/material/Box'
import type { SxProps } from '@mui/material/styles'
import { useTheme } from '@mui/material/styles'

import type { AllSeriesType } from '@mui/x-charts'
import { BarPlot } from '@mui/x-charts/BarChart'
import { ChartContainer as ResponsiveChartContainer } from '@mui/x-charts/ChartContainer'
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight'
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine'
import { ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip'
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis'
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis'
import { AreaPlot, LinePlot, MarkPlot } from '@mui/x-charts/LineChart'

import { stripChartSlotProps } from './chartSlotProps'
import CustomLegend from './linechart/CustomLegend'
import { getMarkerShape } from './linechart/utils/markerShapes'
import { CustomLine } from './linechart/CustomLine'
import { CustomMark, MarkHandlers } from './linechart/CustomMark'
import { CustomTooltip } from './linechart/CustomTooltip'
import { formatCurrency } from '../../../core/utils/NumberFormatting'
import { generateEqualIntervals } from '../../../core/utils/NumberFormatting'

export type LineChartDataset = { x: string; y: number }[]
export type LineChartLabel = string | { label: string; amount: number }
export type TooltipLabel = LineChartLabel & { header?: string }

type LineChartCurve =
  | 'catmullRom'
  | 'linear'
  | 'monotoneX'
  | 'monotoneY'
  | 'natural'
  | 'step'
  | 'stepBefore'
  | 'stepAfter'
  | 'bump'
  | 'bumpX'
  | 'bumpY'

interface LineChartProps {
  areaColor?: string
  axisColor?: string
  baseline?: string
  chartFor?: string
  chartXaxisSx?: SxProps
  chartYaxisSx?: SxProps
  // Because theme chart colors are typed string|undefined
  colors: (string | undefined)[]
  curveType?: LineChartCurve
  customLegendVariant?: string
  customLegendSx?: SxProps
  customLegendBoxSx?: SxProps
  customTooltipLabels?: TooltipLabel[][]
  datasets: LineChartDataset[]
  height?: number
  isCurrency?: boolean
  isGraphClippingXAxis?: boolean
  labels: LineChartLabel[]
  labelBackgroundColor?: string
  labelFontColor?: string
  labelFontSize?: number
  margin?: { top?: number; right?: number; bottom?: number; left?: number }
  markHandlers?: MarkHandlers
  markStyles?: (markIndex: number) => React.CSSProperties & {
    markLabelStyles?: React.CSSProperties
  }
  minRange?: number
  maxRange?: number
  onItemClick?: () => void
  peerAverage?: number
  showArea?: boolean
  showAverage?: boolean
  showAxisHighlight?: boolean
  showBars?: boolean
  showLegend?: boolean
  showMark?: boolean
  showMarkLabel?: boolean
  showTooltip?: boolean
  showXAxis?: boolean
  showXAxisTicks?: boolean
  showYAxis?: boolean
  showYAxisTicks?: boolean
  tickIntervals?: any[] | 'auto' | ((value: any, index: number) => boolean) | undefined
  title?: string
  useCustomMark?: boolean
  valueFormatterString?: string
  width?: number
  xAxisScaleType?: 'band' | 'point'
}

// TODO: Remove negative values and use a proper layout to support X-Charts v8

export const LineChart: React.FC<LineChartProps> = ({
  areaColor,
  axisColor,
  baseline = 'max',
  chartFor,
  chartXaxisSx,
  chartYaxisSx,
  colors,
  curveType = 'linear',
  customLegendVariant,
  customLegendSx,
  customLegendBoxSx,
  customTooltipLabels,
  datasets,
  height,
  isCurrency,
  isGraphClippingXAxis = false,
  labels,
  labelBackgroundColor,
  labelFontColor,
  labelFontSize,
  margin,
  markHandlers,
  markStyles,
  minRange,
  maxRange,
  onItemClick,
  peerAverage,
  showArea = false,
  showAverage = false,
  showAxisHighlight = false,
  showBars = false,
  showLegend = false,
  showMark = true,
  showMarkLabel = false,
  showTooltip = false,
  showXAxis = false,
  showXAxisTicks = false,
  showYAxis = false,
  showYAxisTicks = false,
  title,
  useCustomMark = false,
  valueFormatterString,
  width,
  xAxisScaleType,
}) => {
  const theme = useTheme()

  // Data for the X axis
  const xData = datasets.flat().map((item) => item.x)
  const yData = datasets.flat().map((item) => item.y)

  // Guard the empty-data case: with no points, reduce()/Math.min()/Math.max()
  // produce NaN/±Infinity which flow into the y-axis and make x-charts warn
  // "the value NaN does not exist in the data of y axis". Default to 0 so an
  // empty/loading chart renders a valid (flat) axis instead.
  const average = peerAverage
    ? peerAverage
    : yData.length
      ? Math.round(yData.reduce((value, total) => total + value, 0) / yData.length)
      : 0

  // Used for the min and max Y axis values
  const yMin = yData.length ? Math.floor(Math.min(...yData) / 100) * 100 : 0
  const yMax = yData.length ? Math.ceil(Math.max(...yData) / 100) * 100 : 0

  const series = datasets.map((data, index) => {
    return {
      type: showBars ? 'bar' : 'line',
      curve: curveType,
      color: showBars ? theme.palette.chart?.chart6 : (colors[index] ?? theme.palette.primary.main),
      data: data.map((item) => item.y),
      area: showArea,
      baseline: baseline,
      showMark: true,
      shape: getMarkerShape(index),
      valueFormatter: (value: number) =>
        valueFormatterString ? formatCurrency(value || 0, valueFormatterString) : String(value),
    }
  })

  const defaultMargin = {
    top: 60,
    right: 35,
    bottom: 25,
    left: -10,
  }

  // Merge default margin with any provided custom margin
  const chartMargin = {
    ...defaultMargin,
    ...margin,
  }

  let rangeMin = minRange ?? yMin
  let rangeMax = maxRange ?? yMax
  if (chartFor === 'networthChart') {
    const valueRange = yMax - yMin

    const paddingRatio = valueRange < yMax * 0.05 ? 0.25 : 0.2
    const padding = Math.max(valueRange * paddingRatio, yMax * 0.015)
    rangeMin = yMin - padding < 0 ? yMin : yMin - padding
    rangeMax = yMax + padding
  }

  const getTickInterval = () => {
    if (chartFor === 'creditOverTime') {
      return generateEqualIntervals(rangeMin, 850, 5)
    } else if (chartFor === 'networthChart') {
      return generateEqualIntervals(rangeMin, rangeMax, 5)
    }
    return undefined
  }
  return (
    <Box
      sx={{
        position: 'relative',
        touchAction: 'pan-y', // Allow vertical scrolling
        '& svg': {
          touchAction: 'pan-y', // Allow vertical scrolling on SVG
          userSelect: 'none', // Prevent text selection
        },
        '& .recharts-wrapper': {
          touchAction: 'pan-y', // For Recharts library specifically
        },
      }}
    >
      <ResponsiveChartContainer
        height={height}
        margin={chartMargin}
        series={series as AllSeriesType[]}
        sx={{
          '& .MuiBarLabel-root': {
            fontSize: 10,
            fontWeight: 600,
          },
          '& .MuiAreaElement-root': {
            opacity: 0.15,
          },
          '& .MuiChartsAxis-tickLabel > tspan': {
            fontFamily: theme.typography.body2.fontFamily,
          },
          '& .MuiLineChart-markLabel': {
            fontFamily: theme.typography.body2.fontFamily,
          },
        }}
        width={width}
        xAxis={[{ data: xData, scaleType: xAxisScaleType || (showBars ? 'band' : 'point') }]}
        yAxis={[
          {
            tickInterval: getTickInterval(),
            min: isGraphClippingXAxis ? -22 : rangeMin,
            max: rangeMax || 100,
            colorMap:
              showArea && series.length < 2
                ? {
                    type: 'continuous',
                    min: rangeMin,
                    max: rangeMax,
                    color: [
                      theme.palette.background.paper,
                      colors[0] ?? theme.palette.primary.main,
                    ],
                  }
                : undefined,
            valueFormatter: (value: number) =>
              valueFormatterString ? formatCurrency(value, valueFormatterString) : String(value),
            width: 62,
          },
        ]}
      >
        {/** Provides the average line - positioned first to be behind other elements **/}
        {showAverage && (
          <ChartsReferenceLine
            lineStyle={{
              stroke: labelBackgroundColor
                ? theme.palette.neutral.main
                : theme.palette.neutral.dark,
              strokeDasharray: '10, 8',
              strokeWidth: 2,
            }}
            y={average}
          />
        )}

        {/** Area plot provides gradiant below the line **/}
        {/* Note: Must be above the LinePlot for some style rules to work */}
        <AreaPlot
          onItemClick={onItemClick}
          {...(areaColor && {
            slots: {
              area: (props) => {
                return (
                  <g>
                    <defs>
                      <linearGradient id="customGradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={areaColor} />
                        <stop offset="100%" stopColor={areaColor} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <path {...stripChartSlotProps(props)} fill={`url(#customGradient)`} />
                  </g>
                )
              },
            },
          })}
        />

        {/** Line plot provides the data line **/}
        <LinePlot
          onItemClick={onItemClick}
          slots={{
            line: (props) => (
              <CustomLine
                {...props}
                lineColor={areaColor ?? props.ownerState.color ?? theme.palette.primary.main}
              />
            ),
          }}
        />

        {/** Mark plot provides the circles **/}
        <MarkPlot
          onItemClick={onItemClick}
          slots={
            useCustomMark
              ? {
                  mark: (props) => {
                    return (
                      <CustomMark
                        handlers={markHandlers}
                        labelBackgroundColor={labelBackgroundColor}
                        labelFontColor={labelFontColor}
                        labelFontSize={labelFontSize}
                        showLabel={showMarkLabel}
                        showMark={showMark || props.dataIndex === datasets[0].length - 1}
                        style={markStyles?.(props.dataIndex) ?? {}}
                        valueFormatterString={valueFormatterString}
                        yData={yData}
                        {...props}
                      />
                    )
                  },
                }
              : undefined
          }
        />

        <BarPlot
          barLabel={(item) => (Number(item.value) > 0 ? `$${item.value}` : null)}
          borderRadius={10}
        />

        {/** Provides the X axis line and values **/}
        <ChartsXAxis
          disableLine={!showXAxis}
          disableTicks={!showXAxisTicks}
          slotProps={
            axisColor
              ? {
                  axisLine: {
                    style: {
                      stroke: axisColor,
                    },
                  },
                  axisTick: {
                    style: {
                      stroke: axisColor,
                    },
                  },
                }
              : {}
          }
          sx={{ ...chartXaxisSx }}
        />

        {/** Provides the Y axis values **/}
        <ChartsYAxis
          disableLine={!showYAxis}
          disableTicks={!showYAxisTicks}
          slotProps={
            axisColor
              ? {
                  axisLine: {
                    style: {
                      stroke: axisColor,
                    },
                  },
                  axisTick: {
                    style: {
                      stroke: axisColor,
                    },
                  },
                }
              : {}
          }
          sx={{ ...chartYaxisSx }}
        />

        {/* Add vertical crosshair on hover */}
        {showAxisHighlight && <ChartsAxisHighlight x="line" />}

        {showTooltip && (
          <ChartsTooltipContainer trigger="axis">
            <CustomTooltip
              customLabels={customTooltipLabels}
              isCurrency={isCurrency}
              labels={labels}
              series={series}
              valueFormatterString={valueFormatterString}
            />
          </ChartsTooltipContainer>
        )}
      </ResponsiveChartContainer>

      {showLegend && (
        <CustomLegend
          average={showAverage ? average : undefined}
          customLegendBoxSx={customLegendBoxSx}
          customLegendVariant={customLegendVariant}
          labels={labels}
          series={series}
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            ...customLegendSx,
          }}
          title={title}
          valueFormatterString={valueFormatterString}
        />
      )}
    </Box>
  )
}
